/**
 * Main App Component - VRP System Application
 * Updated to use MUI Toolpad for navigation and Supabase theme
 */

import React from 'react';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { useAuth } from './contexts/AuthContext';
import { AuthWrapper } from './components/auth/AuthWrapper';
import { VRPToolpadAppProvider } from './providers/ToolpadAppProvider';
import { DomainThemeProvider } from './contexts/DomainThemeContext';
import { ContextReadinessProvider } from './contexts/ContextReadinessManager';
import { ReadinessAwareProviders } from './contexts/ReadinessAwareProviders';
import { ThemeReadinessTracker } from './components/readiness/ThemeReadinessTracker';
import { ProjectFeatureRouter } from './components/routing/ProjectFeatureRouter';
import { ReactRouterDiagnostics } from './components/routing/ReactRouterDiagnostics';
import { ParamsTest } from './components/routing/ParamsTest';
import { RouterDebug } from './components/routing/RouterDebug';
// EnhancedNavigation removed - using ProfessionalLayout instead
// Lazy loaded page imports for performance optimization
import { 
  LazyDashboard,
  LazyProjects,
  LazyVehicles,
  LazyLocations,
  LazyJobs,
  LazyOptimization,
  LazyOptimizationResults,
  LazyMapView,
  LazyScheduleView,
  LazyDataManagement,
  LazyScenarioManagement,
  LazyTableEditor,
  LazySettings,
  createLazyRoute,
  preloadCriticalRoutes
} from './components/routing/LazyRoute';

// Critical components that should load immediately
import { ProjectForm } from './pages/ProjectForm';
import { ProjectScenarioDatasetWorkspace } from './pages/ProjectScenarioDatasetWorkspace';
import { IntegrationSimple } from './pages/IntegrationSimple';
import { TestIntegration } from './pages/TestIntegration';
// Edit page imports - keep immediate loading for better UX
import { LocationEditPage } from './pages/LocationEditPage';
import { VehicleEditPage } from './pages/VehicleEditPage';
import { JobEditPage } from './pages/JobEditPage';

// Create lazy routes for less critical pages
const LazyFleetManagement = createLazyRoute(() => import('./pages/FleetManagement'), 'Fleet Management');
const LazyCostModeling = createLazyRoute(() => import('./pages/CostModeling'), 'Cost Modeling');
const LazyGanttDemo = createLazyRoute(() => import('./pages/GanttDemo'), 'Gantt Demo');
const LazyVehicleSummary = createLazyRoute(() => import('./pages/VehicleSummary'), 'Vehicle Summary');
const LazyProductsPage = createLazyRoute(() => import('./pages/ProductsPage'), 'Products');
const LazyShipmentsPage = createLazyRoute(() => import('./pages/ShipmentsPage'), 'Shipments');
const LazySkillsPage = createLazyRoute(() => import('./pages/SkillsPage'), 'Skills');
const LazyEnhancedVehicles = createLazyRoute(() => import('./pages/EnhancedVehicles'), 'Enhanced Vehicles');
const LazyEnhancedVehiclesV2 = createLazyRoute(() => import('./pages/EnhancedVehiclesV2'), 'Enhanced Vehicles V2');
import { featureFlags, shouldUseUnifiedSystem } from './config/featureFlags';
// Infinite Loop Detection and Recovery System
import { InfiniteLoopBoundary } from './components/error/InfiniteLoopBoundary';
import { PerformanceMetrics } from './components/error/PerformanceMetrics';
// Note: Projects component imported above

// Professional theme is now imported from separate file

// Enhanced Vehicles Routing Component with Migration Support
const EnhancedVehiclesWithBypass: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  
  // Debug feature flags
  const bypassEnabled = featureFlags.isEnabled('VITE_FEATURE_ENHANCED_VEHICLES_BYPASS');
  const enhancedEnabled = featureFlags.isEnabled('VITE_FEATURE_ENHANCED_VEHICLES');
  const v2Enabled = featureFlags.isEnabled('VITE_FEATURE_ENHANCED_VEHICLES_V2');
  
  console.log('🔍 [EnhancedVehiclesWithBypass] Feature flag check:', {
    projectId,
    bypassEnabled,
    enhancedEnabled,
    v2Enabled,
    environment: import.meta.env.VITE_ENVIRONMENT,
    shouldUseUnified: shouldUseUnifiedSystem(),
    featureVars: {
      VITE_FEATURE_ENHANCED_VEHICLES: import.meta.env.VITE_FEATURE_ENHANCED_VEHICLES,
      VITE_FEATURE_ENHANCED_VEHICLES_BYPASS: import.meta.env.VITE_FEATURE_ENHANCED_VEHICLES_BYPASS,
      VITE_FEATURE_ENHANCED_VEHICLES_V2: import.meta.env.VITE_FEATURE_ENHANCED_VEHICLES_V2,
      VITE_UNIFIED_ARCHITECTURE: import.meta.env.VITE_UNIFIED_ARCHITECTURE,
      VITE_MIGRATION_PERCENTAGE: import.meta.env.VITE_MIGRATION_PERCENTAGE
    }
  });
  
  // Priority order: Migration System > V2 > Enhanced > Bypass > Standard
  
  // Unified architecture is now the default system
  // V2 is always used in the unified architecture
  if (v2Enabled || shouldUseUnifiedSystem()) {
    console.log('✅ [EnhancedVehiclesWithBypass] Using Unified Architecture - Enhanced Vehicles V2');
    return <LazyEnhancedVehiclesV2 />;
  }
  
  // Check if bypass is enabled
  if (bypassEnabled) {
    console.log('🚩 Enhanced Vehicles bypass enabled, redirecting to standard vehicles');
    return <Navigate to={`/projects/${projectId}/vehicles`} replace />;
  }
  
  // Check if enhanced vehicles feature is enabled
  if (!enhancedEnabled) {
    console.log('🚩 Enhanced Vehicles feature disabled, redirecting to standard vehicles');
    return <Navigate to={`/projects/${projectId}/vehicles`} replace />;
  }
  
  console.log('✅ [EnhancedVehiclesWithBypass] Loading Enhanced Vehicles V1 component (Legacy System)');
  return <LazyEnhancedVehicles />;
};

// Create React Query client with performance optimizations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - Increased for better caching
      gcTime: 15 * 60 * 1000, // 15 minutes - Keep in cache longer
      refetchOnWindowFocus: false, // Reduce unnecessary refetches
      refetchOnMount: false, // Only refetch if stale
      refetchInterval: false, // Disable automatic polling by default
      retry: (failureCount, error) => {
        // Intelligent retry logic
        if (failureCount >= 3) return false;
        if (error instanceof Error && error.message.includes('401')) return false;
        return true;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      networkMode: 'online', // Only run when online
      structuralSharing: true, // Enable structural sharing for better performance
    },
    mutations: {
      networkMode: 'online',
      retry: 1, // Retry mutations once on failure
    },
  },
});

// App Routes component - separated to be closer to Router
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<LazyDashboard />} />
      <Route path="/projects" element={<LazyProjects />} />
      <Route path="/projects/new" element={<ProjectForm />} />
      
      {/* Table Editor routes - flat access to all tables */}
      <Route path="/table-editor" element={<LazyTableEditor />} />
      <Route path="/table-editor/:tableName" element={<LazyTableEditor />} />
      
      {/* Test routes to debug params */}
      <Route path="/test/:testId" element={<ParamsTest />} />
      <Route path="/debug/*" element={<RouterDebug />} />
      <Route path="/projects/:projectId/:feature" element={<ProjectFeatureRouter />} />
      
      {/* Specific sub-routes that need additional parameters (edit/new forms) */}
      <Route path="/projects/:projectId/locations/new" element={<LocationEditPage />} />
      <Route path="/projects/:projectId/locations/:id/edit" element={<LocationEditPage />} />
      <Route path="/projects/:projectId/vehicles/new" element={<VehicleEditPage />} />
      <Route path="/projects/:projectId/vehicles/:id/edit" element={<VehicleEditPage />} />
      <Route path="/projects/:projectId/jobs/new" element={<JobEditPage />} />
      <Route path="/projects/:projectId/jobs/:id/edit" element={<JobEditPage />} />
      
      {/* Legacy route redirects - all routes now require project context */}
      <Route path="/scenarios" element={<Navigate to="/projects" replace />} />
      <Route path="/locations" element={<Navigate to="/projects" replace />} />
      <Route path="/vehicles" element={<Navigate to="/projects" replace />} />
      <Route path="/jobs" element={<Navigate to="/projects" replace />} />
      <Route path="/optimization" element={<Navigate to="/projects" replace />} />
      <Route path="/optimization-results" element={<Navigate to="/projects" replace />} />
      <Route path="/map" element={<Navigate to="/projects" replace />} />
      <Route path="/schedule" element={<Navigate to="/projects" replace />} />
      <Route path="/fleet" element={<Navigate to="/projects" replace />} />
      <Route path="/costs" element={<Navigate to="/projects" replace />} />
      <Route path="/data" element={<Navigate to="/projects" replace />} />
      <Route path="/data-management" element={<Navigate to="/projects" replace />} />
      <Route path="/products" element={<Navigate to="/projects" replace />} />
      <Route path="/shipments" element={<Navigate to="/projects" replace />} />
      <Route path="/skills" element={<Navigate to="/projects" replace />} />
      <Route path="/enhanced-vehicles" element={<Navigate to="/projects" replace />} />
      
      {/* Project-aware vehicle summary route */}
      <Route path="/projects/:projectId/vehicle-summary" element={<LazyVehicleSummary />} />
      
      {/* Standalone routes that don't require project context */}
      <Route path="/settings" element={<LazySettings />} />
      <Route path="/vehicle-summary" element={<Navigate to="/projects" replace />} />
      <Route path="/integration" element={<IntegrationSimple />} />
      <Route path="/test" element={<TestIntegration />} />
      <Route path="/gantt-demo" element={<LazyGanttDemo />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// App content component that uses auth context
const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    return <AuthWrapper />;
  }

  return <AppRoutes />;
};

// Main App component
const App: React.FC = () => {
  // Log provider structure on mount and preload critical routes
  React.useEffect(() => {
    console.log('✅ [App] MUI Toolpad provider structure initialized:', {
      providers: [
        'QueryClientProvider (App.tsx)',
        'Router (App.tsx)', 
        'DomainThemeProvider (provides theme)',
        'VRPToolpadAppProvider (provides Toolpad navigation + VRP contexts)'
      ],
      providerHierarchy: 'Fixed: VRPUnifiedProvider now inside VRPToolpadAppProvider for proper context availability',
      layoutContextFix: 'SmartSecondarySidebar now has access to VRPDataProvider',
      toolpadIntegration: true,
      supabaseTheme: true,
      timestamp: new Date().toISOString()
    });
    
    // Start preloading critical routes after initial render
    preloadCriticalRoutes();
  }, []);

  const handleLoopDetected = (error: Error) => {
    console.error('🚨 [App] Infinite loop detected in main app:', error);
    // You could also send this to an error reporting service
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ContextReadinessProvider>
          <DomainThemeProvider>
            <ThemeReadinessTracker>
              <VRPToolpadAppProvider>
              <InfiniteLoopBoundary 
                onLoopDetected={handleLoopDetected}
                maxUpdatesPerInterval={30}
                intervalMs={100}
              >
                <AppContent />
              </InfiniteLoopBoundary>
              {/* Performance monitoring overlay */}
              <PerformanceMetrics 
                contextNames={['ProjectContext', 'ScenarioContext', 'DatasetContext']}
                showInProduction={false}
              />
              </VRPToolpadAppProvider>
            </ThemeReadinessTracker>
          </DomainThemeProvider>
        </ContextReadinessProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
